import { BY_SYMBOL, printedMassOf } from './elements';

/**
 * Chemical formula parser, shared by molar mass and the equation balancer.
 *
 * Handles element counts, nested parentheses and brackets, hydrates written
 * with a dot or a middot, leading coefficients, and charges. Anything it cannot
 * read it refuses by name rather than guessing.
 */

export class FormulaError extends Error {}

export interface ParsedFormula {
  /** Element symbol to atom count, coefficient already applied. */
  counts: Map<string, number>;
  /** Net charge: +2 for Ca2+, -1 for OH-, 0 for neutral. */
  charge: number;
  /** Leading coefficient, e.g. 2 in "2 H2O". */
  coefficient: number;
}

const MIDDOT = /[·•∙·•∙]/g;

/**
 * Splits a trailing charge off the formula body.
 *
 * Digits before the sign are genuinely ambiguous: the 2 in "Ca2+" is the charge,
 * the 4 in "NH4+" is a subscript. The rules, in order:
 *
 *   1. A caret or a space marks the charge explicitly — "Cr2O7^2-", "SO4 2-".
 *   2. Otherwise, digits are the charge only when what precedes them is a single
 *      element symbol on its own ("Ca2+", "Fe3+"), since "Ca2" is not a species.
 *   3. Otherwise the digits are a subscript and the charge is just ±1 ("NH4+").
 */
function splitCharge(input: string): { body: string; charge: number } {
  const trimmed = input.trim();
  const match = /^(.*?)(\^|\s+)?(\d*)([+-])$/.exec(trimmed);
  if (!match) return { body: trimmed, charge: 0 };

  const [, head = '', separator, digits = '', sign] = match;
  if (!head || /[+-]$/.test(head)) return { body: trimmed, charge: 0 };

  const signed = (n: number) => (sign === '+' ? n : -n);

  if (separator) {
    return { body: head, charge: signed(digits ? Number(digits) : 1) };
  }
  if (!digits) {
    return { body: head, charge: signed(1) };
  }
  if (BY_SYMBOL.has(head)) {
    return { body: head, charge: signed(Number(digits)) };
  }
  return { body: head + digits, charge: signed(1) };
}

interface Cursor {
  text: string;
  i: number;
}

function parseCount(c: Cursor): number {
  let digits = '';
  while (c.i < c.text.length && /\d/.test(c.text[c.i]!)) digits += c.text[c.i++]!;
  return digits ? Number(digits) : 1;
}

function parseGroup(c: Cursor, depth: number): Map<string, number> {
  if (depth > 10) throw new FormulaError('This formula nests too deeply to be real.');
  const counts = new Map<string, number>();

  const add = (symbol: string, n: number) => counts.set(symbol, (counts.get(symbol) ?? 0) + n);

  while (c.i < c.text.length) {
    const ch = c.text[c.i]!;

    if (ch === ')' || ch === ']') {
      if (depth === 0) throw new FormulaError(`Unmatched "${ch}" in the formula.`);
      return counts;
    }

    if (ch === '(' || ch === '[') {
      const close = ch === '(' ? ')' : ']';
      c.i += 1;
      const inner = parseGroup(c, depth + 1);
      if (c.text[c.i] !== close) throw new FormulaError(`Missing "${close}" in the formula.`);
      c.i += 1;
      const multiplier = parseCount(c);
      for (const [symbol, n] of inner) add(symbol, n * multiplier);
      continue;
    }

    if (/[A-Z]/.test(ch)) {
      let symbol = ch;
      c.i += 1;
      while (c.i < c.text.length && /[a-z]/.test(c.text[c.i]!)) symbol += c.text[c.i++]!;
      if (!BY_SYMBOL.has(symbol)) throw new FormulaError(`"${symbol}" is not an element symbol.`);
      add(symbol, parseCount(c));
      continue;
    }

    if (/\s/.test(ch)) {
      c.i += 1;
      continue;
    }

    throw new FormulaError(`Unexpected "${ch}" in the formula.`);
  }

  if (depth > 0) throw new FormulaError('The formula ends with a bracket still open.');
  return counts;
}

/**
 * "CuSO4·5H2O", "Ca(NO3)2", "2 H2O", "SO4 2-" — all read the same way.
 */
export function parseFormula(input: string): ParsedFormula {
  const raw = (input ?? '').replace(MIDDOT, '·').trim();
  if (!raw) throw new FormulaError('No formula was given.');
  if (raw.length > 200) throw new FormulaError('That formula is too long to be real.');

  const { body, charge } = splitCharge(raw);

  // A leading number is a coefficient, not a subscript.
  const lead = /^(\d+)\s*(?=[A-Z([])/.exec(body);
  const coefficient = lead ? Number(lead[1]) : 1;
  if (coefficient === 0) throw new FormulaError('A coefficient of zero means nothing.');
  const withoutCoefficient = lead ? body.slice(lead[0].length) : body;

  // Hydrates: each dot-separated part carries its own multiplier.
  const parts = withoutCoefficient.split('·').filter((p) => p.trim().length > 0);
  if (parts.length === 0) throw new FormulaError('No formula was given.');

  const total = new Map<string, number>();
  for (const part of parts) {
    const trimmed = part.trim();
    const multiplierMatch = /^(\d+)\s*(?=[A-Z([])/.exec(trimmed);
    const multiplier = multiplierMatch ? Number(multiplierMatch[1]) : 1;
    const rest = multiplierMatch ? trimmed.slice(multiplierMatch[0].length) : trimmed;

    const counts = parseGroup({ text: rest, i: 0 }, 0);
    if (counts.size === 0) throw new FormulaError(`"${part.trim()}" has no elements in it.`);
    for (const [symbol, n] of counts) {
      total.set(symbol, (total.get(symbol) ?? 0) + n * multiplier);
    }
  }

  if (total.size === 0) throw new FormulaError('No elements were found in that formula.');

  const scaled = new Map<string, number>();
  for (const [symbol, n] of total) scaled.set(symbol, n * coefficient);

  return { counts: scaled, charge, coefficient };
}

/**
 * Molar mass in g/mol, from the sheet's printed atomic masses. LOCKED: the
 * printed values are used even where they are wrong, because they are what he
 * will have in front of him.
 */
export function molarMass(formula: string): number {
  const { counts } = parseFormula(formula);
  let total = 0;
  for (const [symbol, n] of counts) {
    const mass = printedMassOf(symbol);
    if (mass === null) {
      throw new FormulaError(`The table prints no atomic mass for ${symbol}.`);
    }
    total += mass * n;
  }
  return total;
}

/** Atom counts without the coefficient applied, for the balancer. */
export function atomCounts(formula: string): Map<string, number> {
  const parsed = parseFormula(formula);
  if (parsed.coefficient === 1) return parsed.counts;
  const out = new Map<string, number>();
  for (const [symbol, n] of parsed.counts) out.set(symbol, n / parsed.coefficient);
  return out;
}

export function formatCounts(counts: Map<string, number>): string {
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([symbol, n]) => `${symbol}${n === 1 ? '' : n}`)
    .join('');
}
